<script lang="ts" setup>
    import { computed, ref } from 'vue';
    import { DisplayStyle } from "../Enums/UiEnums";
    import { ValidationState, IValidatorcomponentNameAndId, IComponentErrorMessage } from "../lib/Types/Types";
    import MessageSection from "./Messages/MessageSection.vue";
    import MessageBadge from "./Messages/MessageBadge.vue";
    import { isString } from "lodash"; 

    const states =  ref(new Map<string, { isValid: ValidationState, componentName: string, userErrorMsg?: string}>());

    const props = withDefaults(defineProps<{
        showInvalidMessage?: boolean
    }>(), {
        showInvalidMessage: true
    });

    defineExpose({sinkValidation, isValid});

    const emit = defineEmits<{
        (e: "isValid", state: boolean): void
      }>();


    // Sink validation events
    //  component: Can be a unique name or an object with a name and a unique ID
    //  isValid: either a boolean or 'unmounted' so we know when a component is being destroyed
    //  userErrorMsg: An error message
    function sinkValidation(component: string | IValidatorcomponentNameAndId, isValid?: ValidationState, userErrorMsg?: string): void
    {
        if (undefined === isValid)
        {
            return;
        }

        const componentIdVal = componentId(component);

        if ("unmounted" === isValid)
        {
            states.value.delete(componentIdVal);
            return;
        }
        const currentAllValid = allValid.value;
        states.value.set(componentIdVal, { isValid, userErrorMsg, componentName: componentName(component)  }); 
        if (currentAllValid !== allValid.value) {
            emit("isValid",allValid.value);
        }
    }

    function isValid(): boolean
    {
        return allValid.value;
    }

    function componentId(component: string | IValidatorcomponentNameAndId): string {
        if (isString(component))
            return component;
        return component.id;
    }

    function componentName(component: string | IValidatorcomponentNameAndId): string {
        if (isString(component))
            return component;
        return component.name;
    }


    // See if all the items are valid
    const allValid = computed(() =>{
        for(const state of states.value.values())
        {
            if (!state.isValid)
                return false;
        }
        return true;

    });

    // Get user messages
    const userMessages = computed<IComponentErrorMessage[] | undefined>(() => {
        const messages: IComponentErrorMessage[] = [];
        for(const state of states.value.values())
        {
            if (!state.isValid)
                messages.push({ errorMsg: state.userErrorMsg ?? "Invalid", componentName: state.componentName });
        }

        if (0 == messages.length)
            return;

        return messages;
    });

</script>

<template>
    <template v-if="!allValid">
        <slot name="invalid" :messages="userMessages">
            <MessageSection :displayStyle="DisplayStyle.Danger" :showIcon="false" v-if="showInvalidMessage">
                <MessageBadge :displayStyle="DisplayStyle.Danger">
                    Validation Issue
                </MessageBadge>

                <div v-for="msg in userMessages">
                    <em>{{msg.componentName}}</em>: {{msg.errorMsg}}
                </div>
            </MessageSection>
        </slot>
    </template>
    <template v-if="allValid">
        <slot>
            <MessageBadge :displayStyle="DisplayStyle.Success">Valid</MessageBadge>
        </slot>
    </template>
</template>